import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import PageHeader from '../../components/shell/PageHeader';
import Button from '../../components/ui/Button';
import StatusPill from '../../components/ui/StatusPill';
import { Field, TextInput } from '../../components/ui/Field';
import EmptyState from '../../components/ui/EmptyState';
import { SkeletonBlock } from '../../components/ui/Skeleton';
import Modal from '../../components/overlays/Modal';
import { Select } from '../../components/ui/Field';
import { useOverlays } from '../../components/overlays/OverlayProvider';
import {
  getAgentDetails,
  getReportRefundDetails,
  getSessionDetails,
  getTicketDetails,
  getTransactionDetails,
  getUserDetails,
  postRefund,
} from '../../actions/supportTicketAction';
import { inr } from '../../helpers/format';
import './operations.css';

const PAYMENT_MODES = [
  { value: 'wallet', label: 'Staarvani wallet' },
  { value: 'razorpay', label: 'Razorpay (original method)' },
];

/**
 * Support ticket lookup. Freshdesk owns the ticket list, so this screen resolves a
 * ticket number to the Staarvani records attached to it — the linkage is the point
 * of the screen.
 */
export default function SupportTicket() {
  const dispatch = useDispatch();
  const { toast } = useOverlays();
  const [ticketNumber, setTicketNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [ticket, setTicket] = useState(null);
  const [linked, setLinked] = useState({});
  const [notFound, setNotFound] = useState(false);
  const [refunding, setRefunding] = useState(false);
  const [refund, setRefund] = useState({ amount: '', currency: 'INR', payment_mode: 'wallet' });
  const [busy, setBusy] = useState(false);

  const search = useCallback(async () => {
    if (!ticketNumber.trim()) return;
    setLoading(true);
    setNotFound(false);
    setTicket(null);
    setLinked({});
    try {
      const res = await getTicketDetails(dispatch, ticketNumber.trim());
      const data = res?.data;
      if (!data?.type) {
        setNotFound(true);
        return;
      }
      setTicket(data);

      // Resolve whichever Staarvani record the ticket points at.
      const next = {};
      if (data.created_by) {
        next.user = (await getUserDetails(dispatch, data.created_by))?.data;
      }
      if (data.type === 'report' && data.id) {
        next.refund = (await getReportRefundDetails(dispatch, data.id))?.data;
      }
      if (data.type === 'session' && data.id) {
        next.session = (await getSessionDetails(dispatch, data.id))?.data;
      }
      if (data.type === 'transaction' && data.id) {
        next.transaction = (await getTransactionDetails(dispatch, data.id))?.data;
      }
      if (next.session?.agent_id) {
        next.agent = (await getAgentDetails(dispatch, next.session.agent_id))?.data;
      }
      setLinked(next);
      if (next.refund?.amount) {
        setRefund((r) => ({ ...r, amount: next.refund.amount, currency: next.refund.currency || 'INR' }));
      }
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [dispatch, ticketNumber]);

  const submitRefund = useCallback(async () => {
    setBusy(true);
    try {
      await postRefund(dispatch, {
        user_id: ticket.created_by,
        ticket_id: ticketNumber.trim(),
        amount: Number(refund.amount),
        currency: refund.currency,
        payment_mode: refund.payment_mode,
      });
      toast('Refund processed', { tone: 'success' });
      setRefunding(false);
    } catch {
      toast('Refund failed', { tone: 'error' });
    } finally {
      setBusy(false);
    }
  }, [dispatch, ticket, ticketNumber, refund, toast]);

  return (
    <>
      <PageHeader
        section="Operations"
        title="Support tickets"
        subtitle="Look up a Freshdesk ticket to see the Staarvani records behind it."
      />

      <div className="sv-screen-body">
        <div className="sv-ticket-search">
          <Field label="Ticket number">
            <TextInput
              icon="bx-search"
              placeholder="e.g. 10482"
              value={ticketNumber}
              onChange={(e) => setTicketNumber(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && search()}
            />
          </Field>
          <Button variant="primary" onClick={search} loading={loading} disabled={!ticketNumber.trim()}>
            Look up
          </Button>
        </div>

        {loading && (
          <div className="sv-card" style={{ padding: 18, marginTop: 14 }}>
            <SkeletonBlock width="40%" height={14} />
            <div style={{ marginTop: 12 }}>
              <SkeletonBlock width="70%" height={11} />
            </div>
          </div>
        )}

        {notFound && !loading && (
          <div className="sv-card" style={{ marginTop: 14 }}>
            <EmptyState
              icon="bx-support"
              title="No matching ticket"
              description="That ticket number has no Staarvani record attached to it."
            />
          </div>
        )}

        {ticket && !loading && (
          <div className="sv-ticket">
            <section className="sv-card" style={{ padding: 18 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <h2 className="sv-card__title" style={{ margin: 0 }}>
                  Ticket #{ticketNumber}
                </h2>
                <StatusPill tone="info">{ticket.type}</StatusPill>
              </div>

              <div className="sv-drawer-field">
                <span className="sv-drawer-field__label">Linked record</span>
                <span className="sv-drawer-field__value sv-mono">{ticket.id || '—'}</span>
              </div>
              <div className="sv-drawer-field">
                <span className="sv-drawer-field__label">Raised by</span>
                <span className="sv-drawer-field__value sv-mono">{ticket.created_by || '—'}</span>
              </div>

              {linked.refund?.amount != null && (
                <div style={{ marginTop: 18 }}>
                  <Button variant="primary" onClick={() => setRefunding(true)}>
                    Issue refund
                  </Button>
                </div>
              )}
            </section>

            <aside className="sv-linked-rail">
              {linked.user && (
                <div className="sv-card sv-linked-card">
                  <h3 className="sv-linked-card__title">User</h3>
                  <div className="sv-drawer-field">
                    <span className="sv-drawer-field__label">Name</span>
                    <span className="sv-drawer-field__value">{linked.user.name}</span>
                  </div>
                  <div className="sv-drawer-field">
                    <span className="sv-drawer-field__label">Wallet</span>
                    <span className="sv-drawer-field__value sv-mono">{inr(linked.user.primary_balance)}</span>
                  </div>
                </div>
              )}

              {linked.session && (
                <div className="sv-card sv-linked-card">
                  <h3 className="sv-linked-card__title">Session</h3>
                  <div className="sv-drawer-field">
                    <span className="sv-drawer-field__label">Status</span>
                    <span className="sv-drawer-field__value">{linked.session.status}</span>
                  </div>
                </div>
              )}

              {linked.transaction && (
                <div className="sv-card sv-linked-card">
                  <h3 className="sv-linked-card__title">Transaction</h3>
                  <div className="sv-drawer-field">
                    <span className="sv-drawer-field__label">Amount</span>
                    <span className="sv-drawer-field__value sv-mono">{inr(linked.transaction.amount)}</span>
                  </div>
                  <div className="sv-drawer-field">
                    <span className="sv-drawer-field__label">Status</span>
                    <span className="sv-drawer-field__value">{linked.transaction.status}</span>
                  </div>
                </div>
              )}

              {linked.agent && (
                <div className="sv-card sv-linked-card">
                  <h3 className="sv-linked-card__title">Partner</h3>
                  <div className="sv-drawer-field">
                    <span className="sv-drawer-field__label">Name</span>
                    <span className="sv-drawer-field__value">{linked.agent.name}</span>
                  </div>
                </div>
              )}
            </aside>
          </div>
        )}
      </div>

      <Modal
        open={refunding}
        onClose={() => setRefunding(false)}
        title="Issue refund"
        size="form"
        confirmLabel={`Refund ${inr(refund.amount)}`}
        confirmLoading={busy}
        confirmDisabled={!refund.amount}
        destructive
        onConfirm={submitRefund}
      >
        <p style={{ marginTop: 0 }}>
          This moves money. A wallet refund credits the user immediately; a Razorpay refund
          returns it to their original payment method and cannot be reversed.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Field label="Amount" required>
            <TextInput
              type="number"
              value={refund.amount}
              onChange={(e) => setRefund((r) => ({ ...r, amount: e.target.value }))}
            />
          </Field>
          <Field label="Method" required>
            <Select
              options={PAYMENT_MODES}
              value={refund.payment_mode}
              onChange={(e) => setRefund((r) => ({ ...r, payment_mode: e.target.value }))}
            />
          </Field>
        </div>
      </Modal>
    </>
  );
}
